import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {CarrierService} from '../shared/carrier.service';
import {NavigationService} from '../../shared/services/navigation.service';
import {DataTableColumn} from '../../shared/data-table/data-table.component';
import {SortDirection} from '../../shared/sort-direction';
import {CarrierSearchCriteria} from '../shared/carrier-search-criteria.model';
import {Option} from '../../shared/data-filters/data-filter-select/data-filter-select.component';
import {TmsType} from '../shared/tms-type.model';
import {RegionService} from '../shared/region.service';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {BluechatService} from '../../bluechat/bluechat.service';
import {LoaderService} from '../../shared/services/loader.service';
import {ToastrService} from 'ngx-toastr';
import {ErrorHandlerService} from '../../shared/services/error-handler.service';
import {ModalService} from '../../shared/services/modal.service';
import {UserInfo} from '../shared/user-info.model';
import {CarrierManagerService} from '../shared/carrier-manager.service';
import {finalize} from 'rxjs/operators';
import {Carrier} from '../shared/carrier.model';
import {SearchObjectResult} from '../../shared/search-object-result.model';
import {forkJoin} from 'rxjs';
import {FEATURES, FeatureService} from '../../shared/services/feature.service';
import { NavigationLinkService } from '../../shared/services/navigation-link.service';
import { AuthenticationService } from '../../shared/services/authentication.service';

@Component({
  selector: 'app-carrier-list',
  templateUrl: './carrier-list.component.html',
  styleUrls: ['./carrier-list.component.scss']
})
export class CarrierListComponent implements OnInit {

  tmsTypeOptions: Option[] = [
    { labelKey: 'user-dashboard.carriers.list.filters.tms-type.placeholder', value: undefined },
    { labelKey: 'user-dashboard.tms-type.blujay', value: TmsType.BLUJAY },
    { labelKey: 'user-dashboard.tms-type.blueyonder', value: TmsType.BLUEYONDER },
    { labelKey: 'user-dashboard.tms-type.local', value: TmsType.LOCAL },
  ];

  filters: CarrierSearchCriteria = {};

  @ViewChild('nameColumn', { static: true })
  nameColumn: TemplateRef<any>;

  @ViewChild('codeColumn', { static: true })
  codeColumn: TemplateRef<any>;

  @ViewChild('tmsColumn', { static: true })
  tmsColumn: TemplateRef<any>;

  @ViewChild('lastTmsSynchronizationColumn', { static: true })
  lastTmsSynchronizationColumn: TemplateRef<any>;

  @ViewChild('regionColumn', { static: true })
  regionColumn: TemplateRef<any>;

  @ViewChild('statusColumn', { static: true })
  statusColumn: TemplateRef<any>;

  @ViewChild('actionsColumn', { static: true })
  actionsColumn: TemplateRef<any>;

  @ViewChild('checkboxColumn', { static: true })
  checkboxTemplate: TemplateRef<any>;

  @ViewChild('bluechatColumn', { static: true })
  private bluechatColumn: TemplateRef<any>;

  columns: DataTableColumn[] = [];
  sortColumn: DataTableColumn;
  direction                  = SortDirection.ASC;
  regionsOptions: Option[];

  displayedCarriers: Carrier[]                     = [];
  modalBluechatSelectedCarrierManagers: UserInfo[] = [];
  modalBluechatCode                                = '';
  bluechatEnabled = false;

  modalMultipleBluechatActivateAllAgents = true;

  canManageRegions = false;

  @ViewChild('blueChatModalTemplate')
  private blueChatModalTemplate: TemplateRef<any>;

  @ViewChild('blueChatCarriersModalTemplate')
  private blueChatMultipleModalTemplate: TemplateRef<any>;

  loadPage = (page: number, pageSize: number, sortColumn: DataTableColumn, direction: SortDirection) =>
    this.carrierService.search(page - 1, pageSize, sortColumn.name, direction, this.filters);

  constructor(
    private carrierService: CarrierService,
    private regionService: RegionService,
    private authService: AuthenticationService,
    private navigation: NavigationService,
    public link: NavigationLinkService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private bluechatService: BluechatService,
    private loader: LoaderService,
    private toastr: ToastrService,
    private errorHandler: ErrorHandlerService,
    private modalService: ModalService,
    private carrierManagerService: CarrierManagerService,
    private featureService: FeatureService
  ) {
    this.canManageRegions = this.authService.canManageRegions();
    this.regionService.getOwnRegions().subscribe(regions => {
      const regionsOptions: Option[] = [{
        value: undefined,
        label: this.translate.instant('user-dashboard.carriers.list.filters.region.placeholder')
      }];

      regions.forEach(c => regionsOptions.push({ value: c.id, label: c.name }));

      this.regionsOptions = regionsOptions;
    });
  }

  ngOnInit() {
    const nameColumn = {
      name: 'name',
      displayed: true,
      translationKey: 'user-dashboard.carriers.list.table.name',
      template: this.nameColumn,
      sortable: true,
    };

    this.columns = [
      {
        name: 'checkbox',
        displayed: true,
        translationKey: null,
        template: this.checkboxTemplate,
        sortable: false,
        selectColumn: true,
        onSelect: checked => {
          if (checked) {
            this.selectAllCarriers();
          } else {
            this.unselectAllCarriers();
          }
        },
        notHideable: true,
      },
      nameColumn,
      {
        name: 'code',
        displayed: true,
        translationKey: 'user-dashboard.carriers.list.table.code',
        template: this.codeColumn,
        sortable: true,
      },
      {
        name: 'tms',
        displayed: true,
        translationKey: 'user-dashboard.carriers.list.table.tms',
        template: this.tmsColumn,
        sortable: false,
      },
      {
        name: 'last_tms_synchronization',
        displayed: true,
        translationKey: 'user-dashboard.carriers.list.table.last-tms-synchronization',
        template: this.lastTmsSynchronizationColumn,
        sortable: true,
      },
      {
        name: 'region',
        displayed: true,
        translationKey: 'user-dashboard.carriers.list.table.region',
        template: this.regionColumn,
        sortable: true,
      },
      {
        name: 'status',
        displayed: true,
        translationKey: 'user-dashboard.carriers.list.table.status',
        template: this.statusColumn,
        sortable: true,
      },
      {
        name: 'bluechatColumn',
        translationKey: 'user-dashboard.carriers.list.table.bluechat',
        displayed: true,
        template: this.bluechatColumn,
        sortable: false,
        alignCenter: true,
      },
      {
        name: 'actions',
        displayed: true,
        translationKey: 'user-dashboard.carriers.list.table.actions.label',
        template: this.actionsColumn,
        sortable: false,
        alignRight: true,
        overflowVisible: true,
      },
    ];

    this.sortColumn = nameColumn;
    this.route.queryParams.subscribe(params => {
      this.filters.name = params.name;
      this.filters.code = params.code;
      this.filters.tms_type = params.tms_type;
      this.filters.region_id = params.region_id;

      this.filters.sap_vendor = params.sap_vendor;
      this.filters.by_carrier_code = params.by_carrier_code;
      this.filters.sap_scac = params.sap_scac;
    });

    this.featureService.getFeatureStatusObservable(FEATURES.BLUECHAT).subscribe(bluechatStatus => this.bluechatEnabled = bluechatStatus);
  }

  goToCreateCarrier(): void {
    this.navigation.carriers()
      .create()
      .go();
  }

  goToEditCarrier(id: string) {
    this.navigation.carriers()
      .edit(id)
      .go();
  }

  goToManagers(carrierId: string): void {
    this.navigation.carriers()
      .managers(carrierId)
      .list()
      .go();
  }

  goToCreateManager(carrierId: string): void {
    this.navigation.carriers()
      .managers(carrierId)
      .create()
      .go();
  }

  goToDrivers(carrierId: string): void {
    this.navigation.carriers()
      .drivers(carrierId)
      .list()
      .go();
  }

  goToCreateDriver(carrierId: string): void {
    this.navigation.carriers()
      .drivers(carrierId)
      .create()
      .go();
  }

  goToSubcontractors(carrierId: string): void {
    this.navigation.carriers()
      .subcontractors(carrierId)
      .list()
      .go();
  }

  goToCreateSubcontractor(carrierId: string): void {
    this.navigation.carriers()
      .subcontractors(carrierId)
      .create()
      .go();
  }

  goToTmsSynchronization(carrierId: string): void {
    this.navigation.localLoads()
      .import(carrierId)
      .go();
  }

  exportCarriers() {
    this.carrierService.exportCarriers();
  }

  pageLoaded(result: SearchObjectResult<Carrier>) {
    this.displayedCarriers = result.results;
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: {
          name: this.filters.name,
          code: this.filters.code,
          tms_type: this.filters.tms_type,
          region_id: this.filters.region_id,
          sap_vendor: this.filters.sap_vendor,
          sap_scac: this.filters.sap_scac,
          by_carrier_code: this.filters.by_carrier_code
        }
      }
    );
  }

  enableBluchatForCarriers(activateAllAgents: boolean) {
    this.loader.startLoad();

    const selectedCarriers = this.selectedCarriers();

    let activateBlueChatOnCarrierObs;

    if (!activateAllAgents) {
      activateBlueChatOnCarrierObs = forkJoin(selectedCarriers.map(c => this.bluechatService.enableBluechatOnCarrier(c.id)));
    } else {
      activateBlueChatOnCarrierObs = forkJoin(selectedCarriers.map(c => this.bluechatService.enableBluchatOnCarrierWithAllAgents(c)))
    }

    return activateBlueChatOnCarrierObs
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(
        (_) => {
          this.toastr.success(this.translate.instant('user-dashboard.carriers.form.successes.enable-bluechat-bulk'));

          selectedCarriers.forEach(c => {
            c.bluechatCarrierCode = c.code;
            c.computeEligibleToBluechatActivation();
          });

          this.unselectAllCarriers();
        }, e => this.errorHandler.handleHttpError(e, 'user-dashboard.carriers.form.errors.enable-bluechat-bulk'));
  }

  openEnableBluechatModal(carrier: Carrier) {
    this.modalBluechatCode                    = null;
    this.modalBluechatSelectedCarrierManagers = [];
    this.loader.startLoad();
    this.carrierManagerService.getByCarrier(carrier.id)
      .subscribe( users => {
        this.loader.stopLoad()
        this.modalService.open({
          title: this.translate.instant('bluechat.modal.title'),
          size: 'sm',
          data: {
            users
          },
          content: this.blueChatModalTemplate
        }).subscribe( () => {
          this.enableBluechat(carrier);
        }, e => {
          this.errorHandler.handleHttpError(e, 'user-dashboard.carriers.form.errors.create')
        });
      });
  }

  enableBluechat(carrier: Carrier) {
    this.loader.startLoad();

    this.bluechatService.enableBluechatOnCarrier(carrier.id, this.modalBluechatCode, this.modalBluechatSelectedCarrierManagers)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe( bluechatCompany => {
        this.toastr.success(this.translate.instant('user-dashboard.carriers.form.successes.enable-bluechat'));
        carrier.bluechatCarrierCode = bluechatCompany.code;
        carrier.computeEligibleToBluechatActivation();
      }, e => this.errorHandler.handleHttpError(e, 'user-dashboard.carriers.form.errors.enable-bluechat'));
  }

  selectableCarriers(): Carrier[] {
    return this.displayedCarriers.filter(c => c.isUsingBlueyonderTms && !c.bluechatCarrierCode && !c.blocked);
  }

  selectedCarriers(): Carrier[] {
    return this.selectableCarriers().filter(c => c.selected);
  }

  selectAllCarriers() {
    this.selectableCarriers().forEach(c => c.selected = true);
  }

  unselectAllCarriers() {
    this.selectableCarriers().forEach(c => c.selected = false);
  }

  openMultipleBluechatModal() {
    this.modalService.open({
      title: this.translate.instant('bluechat.modal.title'),
      size: 'sm',
      content: this.blueChatMultipleModalTemplate
    }).subscribe(
      _ => {
        this.enableBluchatForCarriers(this.modalMultipleBluechatActivateAllAgents);
      },
      () => {},
      () => this.modalMultipleBluechatActivateAllAgents = true
    );
  }
}
